import { isRiskMetricId } from '@/constants/ReportDashboard/api-enums'
import { RADashboardPortfolioType } from '@/constants/ReportDashboard/asset-enums'
import { isCardAllowedForDashboardType } from '@/constants/ReportDashboard/card-exclusivity'
import {
  RADashboardCardId,
  RADashboardCardSize,
} from '@/constants/ReportDashboard/cards-enums'
import { isCustomSizeValid } from '@/constants/ReportDashboard/cards-sizes'
import { FADashboardType } from '@/constants/ReportDashboard/dashboards/dashboard-types'
import { RADashboardTabId } from '@/constants/ReportDashboard/tab-enums'
import {
  CardSettings,
  RADashboardCardPrefs,
  RiskMetricsSummaryPrefs,
  RiskMetricsView,
  TabLayout,
  TabSetting,
  VaRPeriod,
} from '@/interfaces/raDashboard.interface'

// ---------------------------------------------------------------------------
// Result type
// ---------------------------------------------------------------------------

export type RADashboardValidationResult =
  | { level: 'strict'; valid: true }
  | { level: 'loose'; valid: true }
  | { level: 'invalid'; valid: false }

// ---------------------------------------------------------------------------
// Primitive helpers
// ---------------------------------------------------------------------------

function isString(val: unknown): val is string {
  return typeof val === 'string'
}

function isStringArray(val: unknown): val is string[] {
  return Array.isArray(val) && val.every(isString)
}

function isStringKeyedRecord<T>(
  val: unknown,
  valueGuard: (v: unknown) => v is T,
): val is Record<string, T> {
  if (typeof val !== 'object' || val === null || Array.isArray(val))
    return false
  return Object.values(val as Record<string, unknown>).every(valueGuard)
}

// ---------------------------------------------------------------------------
// Enum membership sets
// ---------------------------------------------------------------------------

const validPortfolioTypes = new Set<string>(
  Object.values(RADashboardPortfolioType),
)
const validTabIds = new Set<string>(Object.values(RADashboardTabId))
const validCardIds = new Set<string>(Object.values(RADashboardCardId))
const validCardSizes = new Set<string>(Object.values(RADashboardCardSize))

// ---------------------------------------------------------------------------
// Per-type loose guards
// ---------------------------------------------------------------------------

function isTabLayout(val: unknown): val is TabLayout {
  if (typeof val !== 'object' || val === null) return false
  const obj = val as Record<string, unknown>
  return Array.isArray(obj.cardsOrder) && obj.cardsOrder.every(isString)
}

function isTabSetting(val: unknown): val is TabSetting {
  if (typeof val !== 'object' || val === null) return false
  const obj = val as Record<string, unknown>
  return isString(obj.label)
}

function isCardSettings(val: unknown): val is CardSettings {
  if (typeof val !== 'object' || val === null) return false
  const obj = val as Record<string, unknown>
  return isString(obj.size)
}

const validVaRPeriods = new Set<string>(Object.values(VaRPeriod))

function isRiskMetricsView(val: unknown): val is RiskMetricsView {
  if (typeof val !== 'object' || val === null) return false
  const v = val as Record<string, unknown>
  return (
    isString(v.id) &&
    isString(v.name) &&
    isString(v.covarianceMatrix) &&
    typeof v.varConfidenceLevel === 'number' &&
    typeof v.varTimePeriodValue === 'number' &&
    isString(v.varTimePeriodUnit) &&
    validVaRPeriods.has(v.varTimePeriodUnit)
  )
}

// ---------------------------------------------------------------------------
// Strict checks (run only after loose checks pass)
// ---------------------------------------------------------------------------

function passesStrictChecks(obj: Record<string, unknown>): boolean {
  const tabLayouts = obj.tabLayouts as Record<string, TabLayout>
  const tabSettings = obj.tabSettings as Record<string, TabSetting>
  const cardSettings = obj.cardSettings as Record<string, CardSettings>

  // Every suggestedPortfolioType entry must be a valid RADashboardPortfolioType
  if (
    !(obj.suggestedPortfolioType as string[]).every((t) =>
      validPortfolioTypes.has(t),
    )
  )
    return false

  // Every tabsOrder entry must be a valid RADashboardTabId
  if (!(obj.tabsOrder as string[]).every((t) => validTabIds.has(t)))
    return false

  // initialTab must be a valid RADashboardTabId and exist as a key in tabLayouts
  if (!validTabIds.has(obj.initialTab as string)) return false
  if (!((obj.initialTab as string) in tabLayouts)) return false

  // Every tabLayout key must be a valid RADashboardTabId and have a corresponding tabSetting
  const tabLayoutKeys = Object.keys(tabLayouts)
  const tabSettingKeys = new Set(Object.keys(tabSettings))
  if (!tabLayoutKeys.every((k) => validTabIds.has(k) && tabSettingKeys.has(k)))
    return false

  // Every cardsOrder entry must be a valid RADashboardCardId, or an empty spacer (prefix "empty_")
  const allCardIds = Object.values(tabLayouts).flatMap((tl) => tl.cardsOrder)
  if (
    !allCardIds.every((id) => validCardIds.has(id) || id.startsWith('empty_'))
  )
    return false

  // Every cardSettings size must be a valid RADashboardCardSize, 
  // or a custom size that is not smaller than the card's smallest available size.
  const allCardSizesValid = Object.entries(cardSettings).every(([id, setting]) => {
    if (setting.size.startsWith(RADashboardCardSize.Custom)) {
      return isCustomSizeValid(id, setting.size)
    }
    return validCardSizes.has(setting.size)
  })
  if (!allCardSizesValid) {
    return false
  }

  return true
}

// Every card on the dashboard must be allowed for the dashboard's own type.
function hasOnlyCardsMatchingDashboardType(
  tabLayouts: Record<string, TabLayout>,
  dashboardType: FADashboardType,
): boolean {
  const allCardIds = Object.values(tabLayouts).flatMap((tl) => tl.cardsOrder)
  return allCardIds.every((id) => {
    //if it's an empty or unrecognized card, skip it for this dashboardType checking
    if (id.startsWith('empty_') || !validCardIds.has(id)) return true

    //otherwise, confirm the card's type is allowed for this dashboard
    return isCardAllowedForDashboardType(id as RADashboardCardId, dashboardType)
  })
}

// ---------------------------------------------------------------------------
// Public API — single call, three possible outcomes
// ---------------------------------------------------------------------------

/**
 * Validates an unknown value against RADashboardLayout.
 *
 * Returns:
 *   { level: "strict",  valid: true  } — all properties exist, all enum values are valid
 *   { level: "loose",   valid: true  } — all properties exist, but enum values are unverified
 *   { level: "invalid", valid: false } — missing or incorrectly typed properties, or a card
 *                                        on the dashboard doesn't match the dashboard's type
 */
export function validateRADashboardLayout(
  val: unknown,
  dashboardType: FADashboardType,
): RADashboardValidationResult {
  if (
    typeof val !== 'object' ||
    val === null ||
    !isStringArray((val as any).suggestedPortfolioType) ||
    !isStringArray((val as any).tabsOrder) ||
    !isString((val as any).initialTab) ||
    !isStringKeyedRecord((val as any).tabLayouts, isTabLayout) ||
    !isStringKeyedRecord((val as any).tabSettings, isTabSetting) ||
    !isStringKeyedRecord((val as any).cardSettings, isCardSettings) ||
    !hasOnlyCardsMatchingDashboardType((val as any).tabLayouts, dashboardType)
  ) {
    return { level: 'invalid', valid: false }
  }

  const obj = val as Record<string, unknown>

  if (!passesStrictChecks(obj)) {
    return { level: 'loose', valid: true }
  }

  return { level: 'strict', valid: true }
}

// ---------------------------------------------------------------------------
// Card prefs validation
// ---------------------------------------------------------------------------

export function validateCardPrefs(data: unknown): RADashboardCardPrefs | null {
  if (typeof data !== 'object' || data === null) return null

  const obj = data as Record<string, unknown>

  if (typeof obj.dashboardName !== 'string' || !obj.dashboardName) return null

  const result: RADashboardCardPrefs = { dashboardName: obj.dashboardName }

  if (obj.riskMetricsSummary !== undefined) {
    const rms = obj.riskMetricsSummary as Record<string, unknown>
    if (
      Array.isArray(rms?.visibleMetricIds) &&
      rms.visibleMetricIds.every(
        (id) => typeof id === 'string' && isRiskMetricId(id),
      )
    ) {
      const validated: RiskMetricsSummaryPrefs = {
        visibleMetricIds: rms.visibleMetricIds,
      }
      if (Array.isArray(rms.views) && rms.views.every(isRiskMetricsView)) {
        validated.views = rms.views
      }
      if (isString(rms.activeViewId)) {
        validated.activeViewId = rms.activeViewId
      }
      result.riskMetricsSummary = validated
    }
  }

  if (obj.trailingRisk !== undefined) {
    const tr = obj.trailingRisk as Record<string, unknown>
    if (
      typeof tr.selectedDays === 'string' &&
      typeof tr.selectedChart === 'string' &&
      typeof tr.showTable === 'boolean'
    ) {
      result.trailingRisk = {
        selectedDays: tr.selectedDays,
        selectedChart: tr.selectedChart,
        showTable: tr.showTable,
      }
    }
  }

  if (obj.holdingsTopBottomTable !== undefined) {
    const htb = obj.holdingsTopBottomTable as Record<string, unknown>
    const validMetrics = new Set(['securityRisk', 'contributionToRisk'])
    const validShow = new Set(['topBottom', 'topOnly', 'bottomOnly'])
    const validCounts = new Set([5, 10, 15])
    if (
      typeof htb?.metric === 'string' &&
      validMetrics.has(htb.metric) &&
      typeof htb?.showContributors === 'string' &&
      validShow.has(htb.showContributors) &&
      typeof htb?.contributorCount === 'number' &&
      validCounts.has(htb.contributorCount)
    ) {
      result.holdingsTopBottomTable = {
        metric: htb.metric as 'securityRisk' | 'contributionToRisk',
        showContributors: htb.showContributors as
          | 'topBottom'
          | 'topOnly'
          | 'bottomOnly',
        contributorCount: htb.contributorCount as 5 | 10 | 15,
      }
    }
  }

  if (obj.holdingsRiskVisualization !== undefined) {
    const hrv = obj.holdingsRiskVisualization as Record<string, unknown>
    const validViewModes = new Set(['allHoldings', 'topBottom'])
    if (typeof hrv?.viewMode === 'string' && validViewModes.has(hrv.viewMode)) {
      result.holdingsRiskVisualization = {
        viewMode: hrv.viewMode as 'allHoldings' | 'topBottom',
      }
    }
  }

  if (obj.holdingsPortfolio !== undefined) {
    const hp = obj.holdingsPortfolio as Record<string, unknown>
    const validated: RADashboardCardPrefs['holdingsPortfolio'] = {}
    if (
      Array.isArray(hp?.columnState) &&
      hp.columnState.every(
        (s) =>
          typeof s === 'object' &&
          s !== null &&
          typeof (s as Record<string, unknown>).colId === 'string',
      )
    ) {
      validated.columnState = hp.columnState as Record<string, unknown>[]
    }
    if (typeof hp?.filterModel === 'object' && hp.filterModel !== null) {
      validated.filterModel = hp.filterModel as Record<string, unknown>
    }
    if (
      validated.columnState !== undefined ||
      validated.filterModel !== undefined
    ) {
      result.holdingsPortfolio = validated
    }
  }

  if (obj.dailyReturn !== undefined) {
    const dr = obj.dailyReturn as Record<string, unknown>
    if (typeof dr?.selectedChart === 'string') {
      const validated: RADashboardCardPrefs['dailyReturn'] = {
        selectedChart: dr.selectedChart,
      }
      if (isStringArray(dr.hiddenSeries)) {
        validated.hiddenSeries = dr.hiddenSeries
      }
      result.dailyReturn = validated
    }
  }

  if (obj.factorAttribution !== undefined) {
    const fa = obj.factorAttribution as Record<string, unknown>
    const validViews = new Set(['chart', 'table'])
    if (typeof fa?.view === 'string' && validViews.has(fa.view)) {
      const validated: RADashboardCardPrefs['factorAttribution'] = {
        view: fa.view,
      }
      if (isStringArray(fa.hiddenFactors)) {
        validated.hiddenFactors = fa.hiddenFactors
      }
      if (isStringArray(fa.columnOrder)) {
        validated.columnOrder = fa.columnOrder
      }
      result.factorAttribution = validated
    }
  }

  if (obj.attributionHoldings !== undefined) {
    const ah = obj.attributionHoldings as Record<string, unknown>
    const validated: RADashboardCardPrefs['attributionHoldings'] = {}
    if (
      Array.isArray(ah?.columnState) &&
      ah.columnState.every(
        (s) =>
          typeof s === 'object' &&
          s !== null &&
          typeof (s as Record<string, unknown>).colId === 'string',
      )
    ) {
      validated.columnState = ah.columnState as Record<string, unknown>[]
    }
    if (typeof ah?.filterModel === 'object' && ah.filterModel !== null) {
      validated.filterModel = ah.filterModel as Record<string, unknown>
    }
    if (
      validated.columnState !== undefined ||
      validated.filterModel !== undefined
    ) {
      result.attributionHoldings = validated
    }
  }

  if (obj.factorLens !== undefined) {
    const fl = obj.factorLens as Record<string, unknown>
    const validated: RADashboardCardPrefs['factorLens'] = {}
    if (typeof fl?.selectedDataType === 'string') {
      validated.selectedDataType = fl.selectedDataType
    }
    if (typeof fl?.contributionsShown === 'boolean') {
      validated.contributionsShown = fl.contributionsShown
    }
    if (typeof fl?.waterfallShown === 'boolean') {
      validated.waterfallShown = fl.waterfallShown
    }
    if (
      Array.isArray(fl?.factorTableColumnState) &&
      fl.factorTableColumnState.every(
        (s) =>
          typeof s === 'object' &&
          s !== null &&
          typeof (s as Record<string, unknown>).colId === 'string',
      )
    ) {
      validated.factorTableColumnState = fl.factorTableColumnState as Record<string, unknown>[]
    }
    if (typeof fl?.factorTableFilterModel === 'object' && fl.factorTableFilterModel !== null) {
      validated.factorTableFilterModel = fl.factorTableFilterModel as Record<string, unknown>
    }
    if (
      Array.isArray(fl?.topContributorsColumnState) &&
      fl.topContributorsColumnState.every(
        (s) =>
          typeof s === 'object' &&
          s !== null &&
          typeof (s as Record<string, unknown>).colId === 'string',
      )
    ) {
      validated.topContributorsColumnState = fl.topContributorsColumnState as Record<string, unknown>[]
    }
    if (
      Array.isArray(fl?.bottomContributorsColumnState) &&
      fl.bottomContributorsColumnState.every(
        (s) =>
          typeof s === 'object' &&
          s !== null &&
          typeof (s as Record<string, unknown>).colId === 'string',
      )
    ) {
      validated.bottomContributorsColumnState = fl.bottomContributorsColumnState as Record<string, unknown>[]
    }
    if (Object.keys(validated).length > 0) {
      result.factorLens = validated
    }
  }

  return result
}

export function validateRADashboardLayoutJSON(
  json: string,
  dashboardType: FADashboardType,
): RADashboardValidationResult {
  let parsed: unknown
  try {
    parsed = JSON.parse(json)
  } catch {
    return { level: 'invalid', valid: false }
  }
  return validateRADashboardLayout(parsed, dashboardType)
}
